// The alert-cluster card's words and numbers, with no React in sight
// (docs/alert-clusters-refactor.md).
//
// Everything the card SAYS is decided here, for one reason: the feature this
// replaces had its copy scattered through detectors and a hand-kept
// vocabulary map, and four separate times a real finding rendered as generic
// filler because the map and the detectors had drifted apart
// (#334/#341/#342/#347). There is no vocabulary to drift now — nothing is
// model-written — but a card with three states times three rungs still has
// nine ways to read, and `subtitleFor` is exhaustive over all nine by
// construction. A test asserts every pair resolves to a real sentence.

/** Canonical severity LABELS live on the client, keyed by Zabbix level, so
 *  the server can ship levels and a relabel never needs a backfill
 *  (ruling 6). These must match app/lib/severity.py; SEVERITY in severity.ts
 *  is the same table with icons attached. "Critical" is deliberately absent —
 *  it names the fleet-health BAND (High + Disaster), and a level wearing the
 *  band's word would make the two surfaces contradict each other. */
export const SEVERITY_ORDER = [5, 4, 3, 2, 1, 0] as const

// The labels themselves live in severity.ts, keyed by Zabbix level, so the
// server can ship levels and a relabel never needs a backfill. Imported here
// only so the hover readout's accessible name can spell a level out; the
// registry stays the one place a severity is named.
import { SEVERITY, type SeverityKey } from './severity'

export type ClusterState = 'live' | 'recovering' | 'resolved'
export type ClusterRung = 'cluster' | 'narrow' | 'fleet'

export interface HistogramBucket {
  at: number
  by_severity?: Record<string, number> | null
}

/**
 * Were the banded hours busier than this client's usual? (app/lib/cluster_volume)
 *
 * `count` is the alerts in the highlighted hours — the same bars the chart
 * draws. Each of the previous `days` days contributes its BUSIEST stretch of
 * `hours` hours; `rank` is how many of those were quieter than `count`, and
 * `median`/`p25`/`p75` describe their typical size. `verdict` is null when
 * the server would not call it, and `withheld` then says why.
 */
export interface BandVolume {
  from: number
  to: number
  hours: number
  count: number
  days: number
  rank: number
  median: number
  p25: number
  p75: number
  verdict: 'unusual' | 'usual' | null
  withheld: 'short_history' | 'truncated' | null
}

/** One bar's typical range on the prior days, aligned with `histogram`. */
export interface UsualHour {
  at: number
  p25: number
  p75: number
}

export interface AlertClusterRow {
  identity: string
  state: ClusterState
  rung: ClusterRung
  first_alert_at: number
  last_alert_at: number
  recovered_at?: number | null
  alerts_total: number
  hosts_total: number
  alerts_active: number
  hosts_active: number
  by_severity?: Record<string, number> | null
  histogram?: HistogramBucket[] | null
  dominant_label?: string
  dominant_count?: number
  host_group_name?: string
  host_group_total?: number
  shared_tags?: Array<{ tag: string; value?: string }> | null
  cause?: {
    source?: string
    label?: string
    at?: number
    entity?: string
    actor?: string
    names_a_host?: boolean
  } | null
  matches?: Array<{
    identity: string
    first_alert_at: number
    alerts_total: number
    hosts_total: number
    overlap?: number
  }> | null
  /** Every earlier occurrence in the 30 days, however many `matches` lists.
   *  Absent on rows written before it existed, whose list stopped at
   *  LEGACY_MATCH_CAP with nothing saying so. */
  matches_total?: number | null
  /** One entry per day of the 30, oldest first, each `at` the start of a day
   *  on the ENVIRONMENT's clock — the day-sized sibling of `histogram`. */
  match_days?: Array<{ at: number; count: number }> | null
  /** Absent on rows written before it existed, and after a failed baseline
   *  read: the card then says nothing about volume at all. */
  band_volume?: BandVolume | null
  /** Absent when history is too short to say what a typical hour holds. */
  usual_hours?: UsualHour[] | null
  members?: Array<{
    eventid: number
    clock: number
    severity: number
    name: string
    host: string
    recovered_at?: number | null
  }> | null
  members_total: number
  hosts?: string[] | null
  truncated?: boolean
  first_seen?: string | null
  last_seen?: string | null
  computed_at?: string | null
  tz?: string | null
}

const DAY_MS = 86_400_000

/** One clock time on the ENVIRONMENT's zone, zone named. */
export function clockAt(epoch: number, tz?: string | null, withDate = false): string {
  const d = new Date(epoch * 1000)
  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'short',
    ...(withDate || Date.now() - d.getTime() >= DAY_MS
      ? { month: 'short', day: 'numeric' } : {}),
  }
  try {
    return d.toLocaleString([], tz ? { ...opts, timeZone: tz } : opts)
  } catch {
    return d.toLocaleString([], opts)
  }
}

/**
 * One hour label for the chart's x-axis: the clock, never a date and never a
 * zone name.
 *
 * The axis used to be `clockAt(...).slice(0, 5)`, which held only while every
 * bar was younger than a day. It is not: a stored cluster stays on the card
 * until another replaces it (the beat writes nothing when it finds none), so
 * a quiet week leaves a row whose bars are days old — and clockAt dates
 * anything that old, at which point slicing five characters printed "Sep 1"
 * across the whole axis. The zone is named once, in the prose above the
 * chart, and the axis inherits it silently (ruling 11).
 */
export function hourLabel(epoch: number, tz?: string | null): string {
  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }
  const d = new Date(epoch * 1000)
  try {
    return d.toLocaleString([], tz ? { ...opts, timeZone: tz } : opts)
  } catch {
    return d.toLocaleString([], opts)
  }
}

/**
 * The window, which means the ONSETS and nothing else (ruling 11): first
 * alert to last alert. Recovery is a separate sentence and is never called a
 * window — the mockup carried "Event window 37 min" (onset→recovery), "02:04
 * –02:23" (onsets) and "58 alerts in 19 min" all at once, three numbers for
 * two meanings under one word.
 *
 * The zone is named ONCE, on the end of the range, because both ends share
 * it and repeating it reads like two different clocks. A window that spans
 * days dates both ends, or a reader puts the undated end on the dated end's
 * day.
 */
export function formatWindow(
  first: number, last: number, tz?: string | null,
  /** What sits between the two clocks. The dash is right in a meta line,
   *  where the range is read as a label; the facts note reads the same range
   *  as PROSE ("between 09:27 and 09:40 EDT"), and a dash mid-sentence there
   *  is a typographic stumble. One function still decides how both ends are
   *  dated, which is the part that must not fork. */
  sep = '\u2013',
): string {
  // One decision for BOTH ends. clockAt dates anything over a day old on its
  // own, so leaving each half to decide produced "11:33–Sep 4, 11:52 EDT"
  // for a nineteen-minute window — the undated start reads as today and the
  // dated end as last week. Two times side by side get dated the same way or
  // not at all (the same rule as envClock's `withDate`, and the same class of
  // bug as #320/#321).
  const dated = last - first >= 86_400 || Date.now() - first * 1000 >= DAY_MS
  if (last <= first) return clockAt(first, tz, dated)
  const bare: Intl.DateTimeFormatOptions = {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    ...(dated ? { month: 'short', day: 'numeric' } : {}),
  }
  const from = new Date(first * 1000)
  let head: string
  try {
    head = from.toLocaleString([], tz ? { ...bare, timeZone: tz } : bare)
  } catch {
    head = from.toLocaleString([], bare)
  }
  return `${head}${sep}${clockAt(last, tz, dated)}`
}

/** How long the onsets ran, in words. Null when there is nothing to say. */
export function spanWords(seconds: number): string | null {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return s > 0 ? 'under a minute' : null
  const min = Math.round(s / 60)
  if (min < 90) return `${min} minute${min === 1 ? '' : 's'}`
  const h = Math.round(min / 60)
  if (h < 48) return `${h} hour${h === 1 ? '' : 's'}`
  const d = Math.round(h / 24)
  return `${d} day${d === 1 ? '' : 's'}`
}

/**
 * The same duration, abbreviated — for the band's stat line and the scale
 * strip, where "13 minutes" does not fit the region it labels.
 *
 * Never used in prose. Both callers are labels on the chart, sitting beside
 * the full phrase in the headline above them.
 */
export function spanShort(seconds: number): string | null {
  const s = Math.max(0, Math.round(seconds))
  if (s <= 0) return null
  if (s < 60) return '<1 min'
  const min = Math.round(s / 60)
  if (min < 90) return `${min} min`
  const h = Math.round(min / 60)
  if (h < 48) return `${h} h`
  return `${Math.round(h / 24)} d`
}

/**
 * The duration as an ADJECTIVE: "13 minutes" -> "13-minute", for "in this
 * 13-minute window".
 *
 * Null whenever the span has no countable unit to hyphenate ("under a
 * minute"), so the caller drops the qualifier rather than writing "this
 * under a minute window".
 */
export function spanAdjective(seconds: number): string | null {
  const words = spanWords(seconds)
  const m = words && /^(\d+) ([a-z]+?)s?$/.exec(words)
  return m ? `${m[1]}-${m[2]}` : null
}

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`

/**
 * The card's ONE title, at every rung and every state (ruling 10).
 *
 * "Alert cluster detected" was the mockup's headline and it cannot survive
 * the low bar: the same card renders for two alerts on one host, where
 * "cluster" is a claim about something that did not happen. A neutral title
 * with honest numbers under it reads correctly at every size, and there is
 * no rung at which it has to be rewritten.
 */
/**
 * The label on the Outlook slot's tile.
 *
 * RULING 13 SAYS THE CARD DOES NOT CALL ITSELF AI, AND THIS OVERRIDES IT —
 * deliberately, by the product owner, on 2026-09-13. Recorded here so it
 * reads as a decision rather than as drift, and so nobody "corrects" it back:
 *
 *   - Nothing in the alert cluster is model-written. It is computed end to
 *     end, which is exactly what ruling 13 was protecting.
 *   - The posture line that shares this slot IS model-written
 *     (`_environment_insight`), and clients knew this slot as "AI Insight"
 *     before the refactor. Continuity of the name won over the distinction.
 *
 * If that trade is ever revisited, "Netwatch Insight" is the phrasing that
 * keeps the weight without the claim (the thing that COMPUTES is Netwatch —
 * Quark is the one that is addressed).
 */
export const SLOT_LABEL = 'Quark insight'

/* --- the Outlook row ----------------------------------------------------
 *
 * The collapsed row has its own sentence, not a shortened version of the
 * card's. It said "Alert activity" over `subtitleFor` — a neutral title over
 * a prose sentence, in a slot the reader scans rather than reads. The
 * 2026-09-14 design replaces both with the three counts, separated so each
 * can be taken in one fixation, and moves the when and the progress to a
 * second line beneath.
 */

/** The row's tone, and the chip that states it. `accent` is the chip's
 *  alone: it names a process, not a condition, so no bar ever wears it. */
export type TileTone = 'amber' | 'green' | 'accent'

/**
 * The live row's chip and the clause beside its start time (the 2026-09-16
 * design).
 *
 * A live cluster is one whose alerts are still arriving, and whose window the
 * beat will redraw on its next run — so its counts are provisional, which the
 * row now says in words instead of leaving a reader to infer it from a red
 * count that keeps changing.
 */
export const LIVE_CHIP = 'Investigating'
export const LIVE_NOTE = 'Window still being analyzed'

/**
 * The chip on the row.
 *
 * Settled, it is what is happening NOW, as a COUNT: `stateChip` names the
 * phase ("Recovering"), which on the collapsed row told a reader nothing they
 * could act on — every alert cluster is recovering eventually — while the
 * number still open decides whether the row is worth opening now or after
 * coffee.
 *
 * Live, it is the state instead, in the accent. The count moves to the row's
 * third line, where it sits beside the number already cleared; on the chip it
 * was a figure that changed on every beat of a storm still being drawn.
 */
export function tileChip(row: AlertClusterRow): { text: string; tone: TileTone } {
  if (row.state === 'live') return { text: LIVE_CHIP, tone: 'accent' }
  if (row.alerts_active > 0) {
    return { text: `${row.alerts_active} active`, tone: 'amber' }
  }
  // "Cleared", never "all clear": this says the cluster's own alerts came
  // back, and claims nothing about the environment around them.
  return { text: 'Cleared', tone: 'green' }
}

/**
 * The tone the sparkline's highlighted run wears.
 *
 * The chip's own, except while live: the chip then names a process in the
 * accent, and the run is amber — alerts still open, the storm not over —
 * which is the only one of the two that is a statement about the environment.
 */
export function tileTone(row: AlertClusterRow): Exclude<TileTone, 'accent'> {
  const chip = tileChip(row).tone
  return chip === 'accent' ? 'amber' : chip
}

/** `reachParts` without its leading preposition, for the headline's middot
 *  rhythm — "5 hosts" rather than "across 5 hosts". */
export function reachPartsBare(
  row: AlertClusterRow, fleetHosts?: number | null,
): ReachPart[] {
  return reachParts(row, fleetHosts).slice(1)
}

/**
 * The row's headline: what, who, how long — three counts, middot-separated.
 *
 * Counts carry full ink and weight, the units between them recede. The same
 * rule the detail card's finding follows, for the same reason, and built from
 * the same `reachParts`, so a host name is never mistaken for a count.
 *
 * A live cluster gets no duration: its window is still open, and printing one
 * would date an end that has not happened (ruling 11).
 */
export function tileHeadline(
  row: AlertClusterRow, fleetHosts?: number | null,
): ReachPart[] {
  const dot: ReachPart = { text: ' \u00b7 ' }
  // At the narrow rung `reachParts` hands back a HOST NAME, which is the one
  // thing on this row a reader cannot take in at a glance: the row's rhythm
  // is three counts, and an identifier in the middle of it has to be READ
  // rather than scanned. It also competes with the numbers either side of it
  // for the same attention, and loses — it is the longest run on the row and
  // the least comparable.
  //
  // So the row counts, and the dialog names. The headline inside the card
  // still says "6 alerts on bma-mm1", and the group tile beside it repeats
  // the name in its footnote; nothing is lost by the time a reader is
  // looking for it.
  const who: ReachPart[] = row.rung === 'narrow'
    ? [
      { text: String(row.hosts_total), strong: true },
      { text: row.hosts_total === 1 ? ' host' : ' hosts' },
    ]
    : reachPartsBare(row, fleetHosts)
  const out: ReachPart[] = [
    { text: String(row.alerts_total), strong: true },
    { text: row.alerts_total === 1 ? ' alert' : ' alerts' },
    dot,
    ...who,
  ]
  const span = row.state === 'live'
    ? null : spanShort(row.last_alert_at - row.first_alert_at)
  if (span) out.push(dot, { text: span, strong: true })
  return out
}

/**
 * The row's second line: when it happened, and how much of it is over.
 *
 * Two fields rather than one sentence, because the row draws a rule between
 * them: they answer different questions and a reader looking for one should
 * not have to read past the other.
 *
 * Live, the when gets a `note` beside it and the progress drops to a third
 * line of its own. The start is all a live row can date — never a range,
 * which would date an end that has not happened (ruling 11) — and the note is
 * what tells a reader the counts under it are still moving.
 */
export function tileSub(
  row: AlertClusterRow,
): { when: string; progress: string; note?: string } {
  const live = row.state === 'live'
  const when = live
    ? `Since ${clockAt(row.first_alert_at, row.tz)}`
    : formatWindow(row.first_alert_at, row.last_alert_at, row.tz)
  const cleared = Math.max(0, row.alerts_total - row.alerts_active)
  const progress = row.alerts_active === 0
    ? `all ${plural(row.alerts_total, 'alert')} cleared`
    : cleared === 0
      ? `${plural(row.alerts_active, 'alert')} still active`
      : `${cleared} cleared \u00b7 ${row.alerts_active} still active`
  return live ? { when, progress, note: LIVE_NOTE } : { when, progress }
}

/**
 * The caption over the sparkline's highlighted run.
 *
 * Clocks only — no zone and no date. The line directly beneath names the zone
 * once, and a second naming on a row this size reads as two different clocks
 * (the #320/#321 failure in miniature).
 */
export function sparkLabel(row: AlertClusterRow): string {
  const from = hourLabel(row.first_alert_at, row.tz)
  if (row.state === 'live') return `since ${from}`
  return `${from}\u2013${hourLabel(row.last_alert_at, row.tz)}`
}

/* --- the card over the sparkline ----------------------------------------
 *
 * The sparkline has no axis, no scale and no key, because the row it sits on
 * is one thing tall \u2014 so on its own it asks a reader to already know what it
 * is. The card that opens over it says so in words, for a reader of any
 * technical background (PRODUCT.md, 2026-09-15): what a bar is, which bars
 * are picked out and why they wear the colour they do, what the heights are
 * measured against, and which hour the pointer is on.
 *
 * It EXPLAINS and adds no findings. A comparison of the window against the
 * rest of the day was offered for it and declined (2026-09-17): that reading
 * belongs to the card the row opens, which has the axis to back it.
 */

export const SPARK_TITLE = 'Alerts per hour, last 24 hours'
export const SPARK_HOW = 'Each bar is one hour. Taller bars mean more alerts.'
export const SPARK_REST = 'Other hours'
export const SPARK_REST_NOTE = 'Shown for comparison'
/** The card's last line, in the words of however it was opened: a pointer
 *  clicks the row, a keyboard presses Enter on it. */
export const SPARK_CTA = {
  pointer: 'Click to open the full chart',
  keyboard: 'Press Enter to open the full chart',
} as const
/** No column falls in the window when the histogram has not caught up with a
 *  cluster that just started \u2014 see `bandSpan`. */
export const SPARK_NO_BAND =
  'The affected window is too recent to show on this chart yet.'

/** The shortest bar the row draws, as a percentage of its height. An hour
 *  with nothing in it keeps a stub, as it does on the card's chart. */
export const SPARK_FLOOR_PCT = 7

/** The window as the key names it: a range, or only a start while live \u2014
 *  never an end that has not happened (ruling 11). */
export function sparkWindow(row: AlertClusterRow): string {
  return row.state === 'live'
    ? `since ${clockAt(row.first_alert_at, row.tz)}`
    : formatWindow(row.first_alert_at, row.last_alert_at, row.tz)
}

/**
 * Why the picked-out bars wear the colour they do.
 *
 * The colour is NAMED, then its reason, so the key never rests on hue alone.
 * Follows `tileTone` case for case \u2014 a key that said green over amber bars
 * would be worse than no key.
 */
export function sparkToneNote(row: AlertClusterRow): string {
  if (row.state === 'live') return 'Amber: alerts are still arriving'
  if (row.alerts_active > 0) {
    return `Amber: ${plural(row.alerts_active, 'alert')} still active`
  }
  return 'Green: every alert has cleared'
}

/**
 * What the heights are measured against.
 *
 * The row scales its bars to the tallest one and gives every bar a floor, so
 * a quiet hour and a one-alert hour can draw the same stub. The note says so,
 * rather than letting a stub be read as a count.
 */
export function sparkScale(data: Bar[]): string {
  const peak = Math.max(0, ...data.map((b) => b.total))
  const heights = peak === 0
    ? 'No hour on this chart had any alerts.'
    : `Heights are relative to the busiest hour (${plural(peak, 'alert')}), `
      + 'so the shortest bars mean few alerts or none.'
  return `${heights} Information-level alerts are not counted.`
}

/**
 * The whole card as one description, for the row's `aria-describedby`.
 *
 * STATIC, and always referenced: a description attached only once the card
 * opens lands after the row has already been announced, and a screen reader
 * moving by its virtual cursor never opens the card at all. Built from the
 * same words the card shows, so what is heard and what is seen cannot drift.
 * The per-hour readout is left out — it follows a pointer.
 */
export function sparkDescription(row: AlertClusterRow, data: Bar[]): string {
  const band = bandSpan(data)
  const key = band
    ? `${BAND_CAP_LEAD} highlighted, ${sparkWindow(row)}. ${sparkToneNote(row)}.`
    : SPARK_NO_BAND
  const vol = volumeShort(row)
  return [`${SPARK_TITLE}.`, SPARK_HOW, key, vol ? `${vol.text}.` : '', sparkScale(data)]
    .filter(Boolean).join(' ')
}

export interface SparkReadout {
  /** The hour under the pointer as a range, or the whole chart's span. */
  when: string
  /** "3 alerts" / "No alerts"; for the whole chart, its total. */
  count: string
  /** The hour is inside the affected window. */
  inWindow: boolean
}

/**
 * The card's readout: the hour under the pointer, or \u2014 when nothing is under
 * it, which is how a keyboard opens the card \u2014 the span the whole chart
 * covers. Null for an empty chart.
 *
 * An hour reads exactly as the card's own chart reads it (`barReadout`), so
 * the row and the dialog never describe one bar two ways.
 */
export function sparkReadout(
  data: Bar[], index: number | null, row: AlertClusterRow,
): SparkReadout | null {
  if (data.length === 0) return null
  const bar = index === null ? undefined : data[index]
  if (bar) {
    const r = barReadout(bar, row)
    return { when: r.window, count: r.total, inWindow: r.inCluster }
  }
  const total = data.reduce((sum, b) => sum + b.total, 0)
  return {
    // Spaced: both ends of a day-long span are dated, and "12:00–Sep 17"
    // unspaced reads as one token.
    when: formatWindow(
      data[0].at, data[data.length - 1].at + BAR_HOUR_S, row.tz, ' \u2013 ',
    ),
    count: `${total === 0 ? 'No alerts' : plural(total, 'alert')} in ${plural(data.length, 'hour')}`,
    inWindow: false,
  }
}

export const CARD_TITLE = 'Alert activity'

/**
 * The card's primary action, counted.
 *
 * It said "View events", which is the name of a destination rather than a
 * statement of what is waiting there. A reader deciding whether to leave the
 * card wants the size of the thing they are opening, and the number is the
 * one the headline just gave them — so the button reads as the continuation
 * of the sentence above it rather than as generic chrome.
 */
export function viewEventsLabel(row: AlertClusterRow): string {
  return `View ${plural(row.alerts_total, 'alert')}`
}

/**
 * The DEEP LINK's label: how many alerts the incidents page will actually
 * show.
 *
 * IT COUNTED THE WRONG THING. The button said "View 31 alerts" and the page
 * it opens lands on the Live tab, which holds only what is still open — five
 * of them. A control that names a number the destination contradicts is the
 * two-counts bug this card was careful about everywhere else, and it is worse
 * here than on a heading: the reader does not find out until after they have
 * left the card.
 *
 * Null when nothing is open. There is then no number that would be true and
 * nothing at the far end to go to, so the card states the absence instead of
 * offering a link to an empty page (see NO_OPEN_NOTE).
 *
 * This is NOT what the widget's button says. That one expands the member list
 * in place, which holds every alert in the window whether open or not, so it
 * keeps `viewEventsLabel` and its total. Two destinations, two counts, each
 * true of what it opens.
 */
export function viewOpenLabel(row: AlertClusterRow): string | null {
  if (row.alerts_active <= 0) return null
  return `View ${plural(row.alerts_active, 'open alert')}`
}

/**
 * What stands where the deep link would be when nothing is still open.
 *
 * It says what is true OF THE LINK, not of the cluster. "No alerts still
 * open" was the first wording and it put a second green tick forty pixels
 * above the one on `activeLine`, both saying the same thing — the verdict is
 * that line's job, and this slot's job is to account for the missing button.
 */
export const NO_OPEN_NOTE = 'No open alerts to view'

/**
 * What the button will actually do, for the hint beside it.
 *
 * Two things a reader cannot see from the label. That it opens the alerts of
 * THIS WINDOW and not the day's — the chart directly below spends most of its
 * width on 24 hours of context, and a reader who has just read that chart has
 * every reason to expect the button to match it. And that the page shows what
 * is still OPEN, which is why its count is smaller than the headline's.
 */
export function viewEventsHint(row: AlertClusterRow): string {
  const when = row.state === 'live'
    ? `since ${clockAt(row.first_alert_at, row.tz)}`
    : formatWindow(row.first_alert_at, row.last_alert_at, row.tz)
  const open = row.alerts_active
  if (open <= 0) {
    return `All ${plural(row.alerts_total, 'alert')} from this affected `
      + `window (${when}) have recovered.`
  }
  const done = Math.max(0, row.alerts_total - open)
  const head = `Open the ${plural(open, 'alert')} still open from this `
    + `affected window (${when}).`
  // Only when there ARE others: "The other 0 alerts have recovered" is the
  // kind of sentence a generated string produces and a person never would.
  return done > 0
    ? `${head} The other ${plural(done, 'alert')} have recovered.`
    : head
}

/** One run of the reach phrase. `strong` marks the part a reader is scanning
 *  for — the count, or the host that IS the subject at the narrow rung. */
export interface ReachPart { text: string; strong?: boolean }

/**
 * Who the alerts landed on, as parts.
 *
 * The card sets the counts apart by weight, and it has to be told WHICH runs
 * are counts: hunting digits in the finished sentence bolded the `02` inside
 * `nas-synology-02`, which states a count that does not exist. The phrase is
 * assembled once, here, and `reachPhrase` is the same thing joined.
 */
export function reachParts(
  row: AlertClusterRow, fleetHosts?: number | null,
): ReachPart[] {
  if (row.rung === 'narrow') {
    const host = row.hosts?.[0]
    return host
      ? [{ text: 'on ' }, { text: host, strong: true }]
      // The preposition is its own part in EVERY branch, including this one,
      // so `reachPartsBare` can drop it by position rather than by regex on a
      // finished phrase. The joined sentence is unchanged.
      : [{ text: 'on ' }, { text: 'one host' }]
  }
  if (row.rung === 'fleet' && fleetHosts && fleetHosts >= row.hosts_total) {
    // Ruling 19: above the width cap the interesting number is what fraction
    // of the estate is shouting, not the raw count.
    return [
      { text: 'across ' },
      { text: String(row.hosts_total), strong: true },
      { text: ' of ' },
      { text: String(fleetHosts), strong: true },
      { text: ' hosts' },
    ]
  }
  return [
    { text: 'across ' },
    { text: String(row.hosts_total), strong: true },
    { text: row.hosts_total === 1 ? ' host' : ' hosts' },
  ]
}

/** Who the alerts landed on — the phrase the subtitle and the finding share. */
export function reachPhrase(
  row: AlertClusterRow, fleetHosts?: number | null,
): string {
  return reachParts(row, fleetHosts).map((p) => p.text).join('')
}

/** The state chip: what is happening NOW. */
export function stateChip(row: AlertClusterRow): string {
  if (row.state === 'live') return 'Still arriving'
  if (row.state === 'recovering') return 'Recovering'
  return row.recovered_at
    ? `Recovered ${clockAt(row.recovered_at, row.tz)}`
    : 'Recovered'
}

/**
 * The subtitle: the numbers, in a sentence, for all nine (state × rung)
 * pairs. Exhaustive by construction — `state` and `rung` are unions, so a
 * new value of either fails to typecheck here rather than rendering an
 * empty line at a client.
 */
export function subtitleFor(
  row: AlertClusterRow, fleetHosts?: number | null,
): string {
  const alerts = plural(row.alerts_total, 'alert')
  const reach = reachPhrase(row, fleetHosts)
  const when = row.state === 'live'
    // A live cluster's window is still open, so it gets a start, not a range:
    // printing "02:04–02:23" for something still arriving dates an end that
    // has not happened.
    ? `since ${clockAt(row.first_alert_at, row.tz)}`
    : `between ${formatWindow(row.first_alert_at, row.last_alert_at, row.tz)}`
  const head = `${alerts} ${reach} ${when}.`
  if (row.state === 'live') return head
  if (row.state === 'recovering') {
    return `${head} ${plural(row.alerts_active, 'alert')} still active.`
  }
  return row.recovered_at
    ? `${head} Recovered at ${clockAt(row.recovered_at, row.tz)}.`
    : `${head} All alerts have recovered.`
}

/**
 * Which blocks the card renders (ruling 9: the degraded card claims less).
 *
 * A one-host cluster has no "what's SHARED" story — nothing is shared between
 * one host and itself — so those blocks are absent rather than rendered with
 * empty tiles. That absence IS the honesty signal, which is why there is no
 * maturity chip anywhere on this card.
 *
 * `group` LEFT THAT RULE ON 2026-09-15, at the product owner's request. Ruling
 * 9 suppressed it along with `tags`, and the two are not the same kind of
 * claim:
 *
 *   - a shared tag is an assertion ABOUT THE SET — "these hosts have this in
 *     common" — which one host cannot support, so `tags` stays gated;
 *   - a host group is an ATTRIBUTE OF THE HOST. "bma-mm1 is in
 *     virtualweb/windows" is as true of one host as of twenty, and it is the
 *     first thing a reader wants after the host's own name: it says what kind
 *     of machine this was.
 *
 * What a one-host cluster still may not have is the FRACTION. `groupFraction`
 * keeps its gate, so the tile names the group and says the host belongs to it
 * rather than reading "1 of 29 hosts affected" — a ratio whose shape implies
 * a spread that did not happen.
 */
/**
 * May the group be used as the DENOMINATOR in "22 of 34 hosts in this group"?
 *
 * Only when it could actually contain them. The server picks the group most
 * of the cluster's hosts share, which at the fleet rung can be a small
 * minority group — and the tile then read "214 affected · 34 in FileServers",
 * a fraction whose bottom half is smaller than its top. The group is still
 * worth naming; it just stops being a total.
 */
export function groupHoldsCluster(row: AlertClusterRow): boolean {
  return !!row.host_group_name
    && (row.host_group_total ?? 0) >= row.hosts_total
}

export function blocks(row: AlertClusterRow): {
  chain: boolean
  group: boolean
  groupFraction: boolean
  tags: boolean
  cause: boolean
  matches: boolean
  fleetNote: boolean
} {
  const chain = row.rung !== 'narrow'
  return {
    chain,
    group: !!row.host_group_name,
    groupFraction: chain && groupHoldsCluster(row),
    tags: chain && (row.shared_tags?.length ?? 0) > 0,
    cause: !!row.cause?.label,
    matches: (row.matches?.length ?? 0) > 0,
    fleetNote: row.rung === 'fleet',
  }
}

/** Severity levels actually present in the data, worst first — so a client
 *  with no Disaster alerts never sees a Disaster key in the legend. */
export function presentLevels(row: AlertClusterRow): number[] {
  const seen = new Set<number>()
  for (const k of Object.keys(row.by_severity ?? {})) seen.add(Number(k))
  for (const b of row.histogram ?? []) {
    for (const k of Object.keys(b.by_severity ?? {})) seen.add(Number(k))
  }
  return SEVERITY_ORDER.filter((l) => seen.has(l))
}

export interface Bar {
  at: number
  total: number
  /** [level, count] worst-first — the stack order, top down. */
  segments: Array<[number, number]>
  /** This hour overlaps the cluster's own onsets: the highlighted band. */
  inCluster: boolean
}

/**
 * The 24h chart's bars.
 *
 * Empty hours are KEPT (the server ships them), because dropping them draws
 * a quiet night as a narrower day and puts the cluster's band at the wrong
 * place on the axis.
 */
export function bars(row: AlertClusterRow): Bar[] {
  return (row.histogram ?? []).map((b) => {
    const counts = b.by_severity ?? {}
    const segments = SEVERITY_ORDER
      .map((l) => [l, counts[String(l)] ?? 0] as [number, number])
      .filter(([, n]) => n > 0)
    return {
      at: b.at,
      total: segments.reduce((sum, [, n]) => sum + n, 0),
      segments,
      inCluster: b.at + 3600 > row.first_alert_at && b.at <= row.last_alert_at,
    }
  })
}

/** The band's own caption: how many alerts, over how long. */
export function bandLabel(row: AlertClusterRow): string {
  const span = spanWords(row.last_alert_at - row.first_alert_at)
  const alerts = plural(row.alerts_total, 'alert')
  return span ? `${alerts} in ${span}` : alerts
}

/**
 * The lead word on the band's caption.
 *
 * The caption used to sit above the chart, where "58 alerts in 19 minutes"
 * could only be read as a restatement of the headline. Now it sits ON the
 * banded hours — and those hours hold every alert that landed in them, not
 * only the cluster's. A reader who adds up the banded bars and gets a bigger
 * number than the pill has caught us in an arithmetic lie unless the pill
 * says whose number it is. So it says.
 */
export const CHART_TITLE = '24-hour alert activity'

/**
 * The chart's scope line.
 *
 * It names the exclusion because the card's number is NARROWER than the
 * obvious one: a client who opens their Zabbix problem view for the same
 * window can count more events than the card does, and on a real client
 * measured 2026-09-14 that gap was a third of the total. This surface has
 * been burned by exactly that before — a list summing to a cap under a
 * heading reading "58 alerts" — and the rule it settled on is that a count
 * which is narrower than it looks has to say so, once, where it is shown.
 *
 * Here, and only here, because this is where the severities are on display:
 * the legend enumerates what IS counted, so what is not counted belongs
 * beside it. Putting it in the headline or on the collapsed tile would
 * spend the card's one-line slot on a footnote.
 */
export const CHART_WINDOW = 'Severity context \u00b7 Information excluded'

/** Behind the (i) beside the chart's title. It carries what the one-line
 *  scope note has no room for: which levels are counted, and that the
 *  24 hours are CONTEXT for a window that is much shorter than they are. */
export const CHART_HINT =
  'Every alert raised in the last 24 hours, stacked by severity. '
  + 'Information-level events are not counted, here or anywhere on this card. '
  + 'The highlighted region is the cluster this card is about. '
  + 'Where it is shown, the pale band behind each bar is that hour\u2019s usual '
  + 'range: the middle half of the same hour over the previous four weeks.'

/**
 * The lead on the region drawn across the cluster's own hours.
 *
 * It said "This cluster", which named the region correctly and told a reader
 * nothing they could act on. "Affected window" is the phrase the rest of the
 * card now uses for the same hours — the button, the findings heading and the
 * scale strip all say it — so the region, its callout and the three places
 * that refer to it are one vocabulary instead of four.
 */
export const BAND_CAP_LEAD = 'Affected window'

/**
 * The callout's third line: what landed in the banded hours, in three counts.
 *
 * The same three numbers the headline states, abbreviated. A restatement is
 * the point: the callout is what a reader lands on when they look at the
 * chart first, and sending them back up the card to find out what the blue
 * region means is the whole failure the callout exists to fix.
 */
export function bandStats(row: AlertClusterRow): string {
  const span = spanShort(row.last_alert_at - row.first_alert_at)
  return [
    plural(row.alerts_total, 'alert'),
    plural(row.hosts_total, 'host'),
    row.state === 'live' ? null : span,
  ].filter(Boolean).join(' \u00b7 ')
}

/** The label on the scale strip's context run: what the 24 hours are FOR.
 *  They are not the finding; the marked slice is. */
export const SCALE_CONTEXT = '24-hour view (context)'

/**
 * Where the scale strip's context label goes — the longer of the two runs the
 * affected window leaves free.
 *
 * THE STRIP SHIPPED SAYING SOMETHING FALSE. It was two segments split at the
 * window's left edge, the right one labelled with the window's duration — so
 * a 13-minute cluster at 09:27 had "13 min" spanning 09:00 to the end of the
 * chart, three hours of it, and the caption beneath centred on that rather
 * than on the cluster. It only read correctly when the cluster happened to
 * sit against the right edge, which is where the design's example sat.
 *
 * The strip is a RULER for the chart above it: the bar is the whole day and
 * the slice is the window, at the window's own columns. That leaves a run
 * free on each side, and the label goes in the longer one — a label centred
 * across the whole bar would collide with a slice in the middle of the day.
 *
 * Null when neither run can hold it, which is a cluster covering nearly the
 * whole day: the bar and its slice still draw, and the caption under the
 * slice still names it.
 */
export function contextRun(
  band: BandSpan | null, count: number,
): { start: number; end: number } | null {
  if (count <= 0) return null
  if (!band) return { start: 0, end: count }
  const left = band.start
  const right = count - (band.end + 1)
  // Four columns is a sixth of the day, which the label can hold once it is
  // allowed to ellipsise. Below that it would be two characters and a rule.
  if (Math.max(left, right) < 4) return null
  return left >= right
    ? { start: 0, end: band.start }
    : { start: band.end + 1, end: count }
}

/**
 * The run the window's DURATION sits in: the free run `contextRun` did not
 * take, with the direction its arrow points to reach the slice.
 *
 * The run the window's DURATION sits in, and which side of the slice it is.
 *
 * The design prints "13 min" between the two arrows that bracket the window,
 * inside its own segment of the strip — which holds text only because the
 * mockup drew the cluster as a fifth of the day. Placed truthfully the window
 * is usually one column of twenty-four and holds none, so the words move out
 * to the free run beside it, just past the arrow on that side. The arrows
 * themselves stay on the window in every case: they are what marks it, and
 * they are drawn beside its rules rather than laid out in a column, so no
 * amount of narrowness takes them away.
 *
 * Two columns is the floor for the words. Below that they would print on the
 * arrow, and the caption under the strip carries the duration instead.
 */
export function markerRun(
  band: BandSpan | null, count: number,
): { start: number; end: number; side: 'left' | 'right' } | null {
  if (!band || count <= 0) return null
  const left = band.start
  const right = count - (band.end + 1)
  // contextRun takes the longer run and gives ties to the left, so this is
  // always the other one of the two.
  const takeRight = left >= right
  if ((takeRight ? right : left) < 2) return null
  return takeRight
    ? { start: band.end + 1, end: count, side: 'right' }
    : { start: 0, end: band.start, side: 'left' }
}

/**
 * The chart's y gridlines, and the ceiling the bars are drawn against.
 *
 * The bars used to be scaled to the tallest bar, which makes every chart
 * look equally busy: a night with a peak of three and a storm peaking at
 * ninety drew the same silhouette. A rounded ceiling with labelled lines
 * gives the height a unit, and the numbers on the left are what let a reader
 * say the banded hours were twice the day's normal rather than merely taller.
 *
 * Steps are 1/2/5 x a power of ten, never below 1 — a gridline at half an
 * alert counts something that cannot happen.
 */
export function yTicks(peak: number): { max: number; ticks: number[] } {
  const p = Math.max(1, Math.ceil(peak))
  const raw = p / 3
  const mag = 10 ** Math.floor(Math.log10(Math.max(raw, 1)))
  const step = Math.max(1, [1, 2, 5, 10].map((m) => m * mag).find((c) => c >= raw) ?? mag * 10)
  const max = Math.ceil(p / step) * step
  const ticks: number[] = []
  for (let v = 0; v <= max; v += step) ticks.push(v)
  return { max, ticks }
}

/** Where the band sits on the chart: the first and last column it covers,
 *  and which of its edges the caption hangs from.
 *
 *  The columns are contiguous by construction — `inCluster` is a time range,
 *  not a predicate over counts — so a first and a last index describe the
 *  band exactly, and a quiet hour in the middle of a storm stays inside it.
 *
 *  The anchor exists because the caption is wider than a narrow band. Centred
 *  on a one-hour cluster at 23:00 it would hang off the chart entirely, so a
 *  band near either edge pins the caption to that edge instead. Returns null
 *  when no column is in the cluster, which happens when the histogram has not
 *  caught up with a cluster that just started.
 */
export interface BandSpan {
  start: number
  end: number
  anchor: 'start' | 'mid' | 'end'
}

export function bandSpan(data: Bar[]): BandSpan | null {
  const start = data.findIndex((b) => b.inCluster)
  if (start < 0) return null
  let end = start
  for (let i = start; i < data.length; i += 1) if (data[i].inCluster) end = i
  return { start, end, anchor: edgeAnchor((start + end + 1) / 2 / data.length) }
}

/**
 * Which edge a floating label hangs from, given its centre as a fraction of
 * the plot's width.
 *
 * Shared by the band's caption and the hover readout, because both are wider
 * than the column they point at and both hang off the chart at 23:00 if
 * centred. One rule, so the two never disagree about where the edge is.
 */
export function edgeAnchor(mid: number): 'start' | 'mid' | 'end' {
  return mid < 0.28 ? 'start' : mid > 0.72 ? 'end' : 'mid'
}

/** The same rule for a label pointing at ONE column. */
export function columnAnchor(index: number, count: number): 'start' | 'mid' | 'end' {
  return edgeAnchor((index + 0.5) / Math.max(1, count))
}

/* --- the hover readout --------------------------------------------------
 *
 * What one bar says when a reader asks it. This used to be the browser's own
 * `title` attribute, which is a tooltip belonging to no design system: it
 * takes the OS font and colours, waits a second to appear, cannot be styled,
 * never appears on touch at all, and is unreachable from the keyboard. It
 * also could not hold the one thing worth showing — the per-severity split
 * that the stacked bar encodes but cannot be read off precisely.
 */

/** One bar covers an hour. Named, because the readout states a RANGE. */
export const BAR_HOUR_S = 3600

export interface BarReadout {
  /** The hour the bar covers, as a range. A bar is not an instant, and the
   *  axis already prints hour starts — "00:00 EDT" over a bar invited
   *  reading the whole hour's alerts as having landed on the stroke. */
  window: string
  /** "14 alerts" / "1 alert" / "No alerts" — a quiet hour says so in words,
   *  because a readout that only ever names a number cannot say zero. */
  total: string
  /** Worst-first, only the levels present in this hour. */
  levels: Array<{ level: number; count: number }>
  /** This hour is inside the cluster's own onsets — the banded region. */
  inCluster: boolean
  /** "Usually 3–9 at this hour", or null where there is no underlay. */
  usual: string | null
}

export function barReadout(bar: Bar, row: AlertClusterRow): BarReadout {
  const u = usualAt(row, bar.at)
  return {
    usual: u ? `Usually ${range(u.p25, u.p75)} at this hour` : null,
    // formatWindow, not two clockAt calls: it decides for BOTH ends whether
    // to date them, and a bar days old with one dated end is the #320/#321
    // bug in miniature.
    window: formatWindow(bar.at, bar.at + BAR_HOUR_S, row.tz),
    total: bar.total === 0 ? 'No alerts' : plural(bar.total, 'alert'),
    levels: bar.segments.map(([level, count]) => ({ level, count })),
    inCluster: bar.inCluster,
  }
}

/**
 * The readout as one sentence, for the column's accessible name.
 *
 * Built from `barReadout`, so what a screen reader hears and what the tooltip
 * shows cannot drift into two descriptions of one bar.
 */
export function barLabel(bar: Bar, row: AlertClusterRow): string {
  const r = barReadout(bar, row)
  const split = r.levels
    .map((x) => `${x.count} ${SEVERITY[x.level as SeverityKey]?.label ?? `level ${x.level}`}`)
    .join(', ')
  return [r.window, r.total, split, r.inCluster ? 'in this cluster' : '', r.usual ?? '']
    .filter(Boolean).join(' · ')
}

/**
 * The still-open clause with its count lifted out, so the card can set the
 * number apart from the words: `{ count: 1, text: 'still open on 1 host.' }`.
 *
 * Null when nothing is open — there is no count to lift, and that case reads
 * as `activeLine`'s own sentence.
 *
 * It exists so the two renderings cannot drift: `activeLine` is built from
 * this rather than repeating the host arithmetic, which is how the same
 * number ends up phrased two ways on one surface.
 */
export function openClause(
  row: AlertClusterRow,
): { count: number; text: string } | null {
  if (row.alerts_active === 0) return null
  const hosts = row.hosts_active === 1 ? '1 host' : `${row.hosts_active} hosts`
  return { count: row.alerts_active, text: `still open on ${hosts}.` }
}

/** What the still-active block says, as one sentence. */
export function activeLine(row: AlertClusterRow): string {
  const open = openClause(row)
  if (!open) return 'All related alerts have recovered.'
  return open.text.charAt(0).toUpperCase() + open.text.slice(1)
}

/* --- the volume verdict ------------------------------------------------
 *
 * Whether the highlighted hours were busier than this client's usual. The
 * server decides (app/lib/cluster_volume); these functions only word it, and
 * every number they print is one the server sent.
 *
 * THE COMPARISON IS PEAK AGAINST PEAK. The card always shows the day's
 * biggest storm, so its hours are the day's busiest by construction. Each
 * previous day is therefore represented by ITS busiest stretch of the same
 * length, never by the same clock hours: comparing a maximum with ordinary
 * hours called a third of all cards unusual in the replay. The copy has to
 * say "busiest hour" for the same reason, or a reader will check the number
 * against the underlay (which IS same-hour) and find it does not match.
 */

/** "a–b", or one number when the range has collapsed. */
function range(a: number, b: number): string {
  return a === b ? String(a) : `${a}\u2013${b}`
}

/** "busiest hour" / "busiest 3-hour stretch". */
export function stretchWords(hours: number): string {
  return hours <= 1 ? 'busiest hour' : `busiest ${hours}-hour stretch`
}

/** "the highlighted hour" / "the 3 highlighted hours". */
function bandWords(hours: number): string {
  return hours <= 1 ? 'the highlighted hour' : `the ${hours} highlighted hours`
}

/**
 * The verdict's words, shared by the dialog's lead and the tile.
 *
 * The READER is the client: their own staff open this card. So the copy
 * speaks to them ("your"), or needs no subject at all ("than usual" already
 * means their usual). It said "for this client" at first, which is Netwatch
 * describing a customer to itself, on the customer's own screen. The copy
 * guard in clusterCard.test.ts fails on any verdict string that says "client".
 */
export const VOLUME_UNUSUAL = 'Busier than usual'
export const VOLUME_USUAL = 'Within your usual range'

export interface VolumeLine {
  tone: 'unusual' | 'usual'
  lead: string
  text: string
}

/**
 * The dialog's verdict line, under the finding. Null when the server made no
 * call (legacy row, failed read, or withheld — the last is `volumeGap`'s).
 *
 * The usual case has three wordings because "usual" covers three different
 * positions, and a single sentence would contradict its own numbers for two
 * of them: at or under the typical range; above it but matched by several
 * past days; or the most in the window but too close to a typical day to
 * count (the floor).
 */
export function volumeLine(row: AlertClusterRow): VolumeLine | null {
  const v = row.band_volume
  if (!v || !v.verdict) return null
  const lead = `${plural(v.count, 'alert')} in ${bandWords(v.hours)}`
  const peak = stretchWords(v.hours)
  const typical = range(v.p25, v.p75)
  if (v.verdict === 'unusual') {
    return {
      tone: 'unusual',
      lead: VOLUME_UNUSUAL,
      text: `${lead}, more than the ${peak} on ${v.rank} of the previous `
        + `${plural(v.days, 'day')} (usually ${typical}).`,
    }
  }
  const higher = v.days - v.rank
  let text: string
  if (v.count <= v.p75) {
    text = `${lead}; on a typical day your ${peak} holds ${typical}.`
  } else if (higher > 0) {
    text = `${lead}, above a typical day\u2019s ${peak} (${typical}), but `
      + `${higher} of the previous ${plural(v.days, 'day')} matched or beat it.`
  } else {
    text = `${lead}, the most in ${plural(v.days, 'day')}, but only `
      + `${plural(v.count - v.median, 'alert')} above a typical day\u2019s ${peak} (${v.median}).`
  }
  return { tone: 'usual', lead: VOLUME_USUAL, text }
}

/** The tile's short verdict. Both cases are stated: the reassuring one is
 *  worth as much as the alarm on a card that exists almost every day. */
export function volumeShort(row: AlertClusterRow): { tone: 'unusual' | 'usual'; text: string } | null {
  const v = row.band_volume
  if (!v || !v.verdict) return null
  return v.verdict === 'unusual'
    ? { tone: 'unusual', text: VOLUME_UNUSUAL }
    : { tone: 'usual', text: VOLUME_USUAL }
}

export const NO_VOLUME_TITLE = 'No comparison with usual volume'

/** Why there is no verdict, as a check row: the rule, never a guess. */
export function volumeGap(row: AlertClusterRow): { title: string; note: string } | null {
  const v = row.band_volume
  if (!v || v.verdict || !v.withheld) return null
  const note = v.withheld === 'truncated'
    ? 'The monitoring read hit its row limit, so this count may be low and '
      + 'cannot be called usual.'
    : `Needs at least 14 days of alert history to compare with; `
      + `${v.days === 0 ? 'none is recorded yet' : `${plural(v.days, 'day')} so far`}.`
  return { title: NO_VOLUME_TITLE, note }
}

/** The usual range of the bar starting at `at`, if the server sent one. */
export function usualAt(row: AlertClusterRow, at: number): UsualHour | null {
  return row.usual_hours?.find((u) => u.at === at) ?? null
}

/** Each bar's underlay, aligned with `bars(row)`; null without one. */
export function usualBand(row: AlertClusterRow, data: Bar[]): Array<UsualHour | null> | null {
  if (!row.usual_hours?.length) return null
  return data.map((b) => usualAt(row, b.at))
}

/** The ceiling the chart must fit: the tallest bar or the tallest underlay,
 *  so a quiet day's usual range is never drawn off the top. */
export function chartPeak(data: Bar[], usual: Array<UsualHour | null> | null): number {
  const bar = Math.max(1, ...data.map((b) => b.total))
  const band = Math.max(0, ...(usual ?? []).map((u) => u?.p75 ?? 0))
  return Math.max(bar, band)
}

/* --- the feedback row ---------------------------------------------------
 *
 * WHAT THIS ROW PROMISES CHANGED ON 2026-09-14, BY THE PRODUCT OWNER, in the
 * card design. Recorded here rather than left to read as drift:
 *
 *   - Ruling 14 said the row is a message to the Netwatch team and nothing
 *     more, because nothing reads these answers back to change what any
 *     client sees. The copy said so ("It does not change what this page
 *     shows"), on the principle that a promise a client notices going unkept
 *     costs more than the one it was meant to buy.
 *   - The design's copy says the answers help improve future correlations.
 *     That is a forward-looking claim about the product, not about this
 *     card, and the second sentence still refuses the thing a reader would
 *     otherwise assume — that answering re-runs the analysis in front of
 *     them.
 *
 * If it is ever revisited, "Your feedback goes to the Netwatch team" keeps
 * the invitation without claiming a loop that does not exist yet.
 */
export const FEEDBACK_TITLE = 'Was this useful?'
export const FEEDBACK_NOTE =
  'Your feedback helps improve future correlations. '
  + "It won't change this result."

/**
 * The two enrichment tiles when the server had nothing to put in them.
 *
 * Both say what was LOOKED FOR, not merely that nothing was found. "No
 * possible cause" on its own reads as a verdict on the environment — as
 * though Netwatch had weighed the question and cleared it. Naming the rule
 * (45 minutes before the first alert; the same leading alert and half the
 * same hosts) hands the reader what they need to judge the gap themselves,
 * and keeps the tile from being a shrug in the middle of the chain.
 *
 * Neither claims WHICH kind of empty this is, because the card cannot tell
 * them apart: a client with no stored history and a client whose history
 * holds nothing similar both arrive here as an empty list.
 *
 * They used to title a TILE each, under a label that named the question
 * ("Possible cause" / "Seen before"). The tiles are gone — an absence in a
 * bordered box the same shape as a finding read, across the room, as a
 * finding — so each note now rides a lead that carries its own question
 * (NO_CAUSE_LEAD / NO_MATCH_LEAD) on one line at the weight of a footnote.
 * The rules themselves are unchanged, and still stated.
 */
// The sources this names must be sources that can actually produce a row.
// It said "template edit or config commit" — but the config-backup half was
// never wired (`gitea_events` has no caller), so the tile stated a rule
// Netwatch does not follow and invited a reader to check a shelf that was
// never stocked. What it really reads is the Zabbix audit log, filtered to
// host, template, script, action and discovery-rule changes; the three named
// here are ~95% of what a live client actually writes.
//
// Process appearances (the census `process_appeared` source) are deliberately
// NOT named yet: the lane exists but enrolment reaches hosts over a week or
// more, so naming it now would repeat the same overclaim in a new place. Add
// it once a real client is delivering samples.
export const NO_CAUSE_NOTE =
  'No host, template or script change in the 45 minutes before the first alert.'
export const NO_MATCH_NOTE =
  'Matching needs the same leading alert and at least half the same hosts, '
  + 'within 30 days.'

/**
 * What each check is CALLED, now that the absences have a band of their own.
 *
 * They were two lower-case leads on one line under "Quark also looked for" —
 * the object of a sentence, at a footnote's weight. The 2026-09-14 design
 * gives them a labelled band ("Additional checks") and a row each, so each
 * title stands on its own and is set in sentence case, like a heading.
 *
 * THIS DROPS THE 2026-09-14 OVERRIDE THAT MADE THE LINE QUARK'S, because the
 * design removes the sentence that carried it: there is no actor in
 * "Additional checks". Nothing in this band names Netwatch or Quark, and
 * ruling 13 still holds — the card does not call itself AI.
 *
 * Neither title may claim the environment is fine. The absence of a recorded
 * change is not an all-clear, and this is the wrong surface to imply one:
 * each title states what was not found and the note beneath it states the
 * rule that was applied.
 */
export const NO_CAUSE_TITLE = 'No recent configuration change'
export const NO_MATCH_TITLE = 'No similar pattern in the past 30 days'

/**
 * The match check's inline note.
 *
 * Short because the two checks share one row and the rule is long. The rule
 * is not lost: it is NO_MATCH_NOTE, behind the (i) on that row, and that is
 * the only reason this brief is allowed to exist. An absence stated without
 * its rule reads as a verdict on the environment, which is the exact failure
 * the notes were written to prevent.
 */
export const NO_MATCH_BRIEF = 'No matching alert pattern found.'

/** The label over the checks band. */
export const CHECKS_LABEL = 'Additional checks'

/** Stated, never hidden: after a capped read every count is a floor. */
export const TRUNCATED_NOTE =
  'The monitoring read hit its row limit, so these counts are the minimum — '
  + 'the real totals may be higher.'

/** The fleet rung's note, where the per-host detail stops being useful. */
export function fleetNote(
  row: AlertClusterRow, fleetHosts?: number | null,
): string {
  const of = fleetHosts && fleetHosts >= row.hosts_total
    ? ` of ${fleetHosts}` : ''
  return `${row.hosts_total}${of} hosts alerted — too many to list, so the `
    + 'blast radius is shown instead of per-host detail.'
}

/* --- what the card found, and what it did not --------------------------
 *
 * These two functions replace the chain of four tiles and the two side
 * tiles. The split they encode is the whole point of the redesign:
 *
 *   facts()  — questions that came back with an answer. Each gets a column.
 *   gaps()   — questions that came back empty. Together they get one line.
 *
 * Before, both kinds rendered as the same object: a bordered, washed,
 * icon-bearing tile. Two of the four chain rungs on a typical card held an
 * absence, so roughly 40% of the card's area was spent saying nothing was
 * found — and an absence drawn at the weight of a finding reads as a finding
 * from across the room. The old CSS said as much in a comment and then gave
 * the empty tile the same box anyway.
 *
 * What does NOT change: every question the card asks is still visible in its
 * answer or in the gap line, with its rule stated. Ruling 9 also survives —
 * a `narrow` cluster still has no connective story, so group and tags are
 * absent rather than rendered empty.
 */

/** Which question a fact answers. The key is stable so a caller can style or
 *  test one row without matching on its label. */
export type FactKey = 'dominant' | 'group' | 'tags' | 'cause' | 'matches'

/**
 * The hue a fact's tile wears.
 *
 * COLOUR'S JOB ON THIS CARD WIDENED ON 2026-09-14, in the design. It had
 * exactly one — the severity scale, in the chart's bars and the event list's
 * dots — because a tone per QUESTION had already been tried and withdrawn:
 * it was spent on a chain of four rungs of which two usually held an
 * ABSENCE, and a pastel field whose emptiest boxes are as loud as its
 * findings scans as findings.
 *
 * What makes the tones affordable again is that an absence no longer gets a
 * tile at all — the "Additional checks" band at the foot of the card holds
 * those — so every tinted square here sits beside something that was found.
 *
 * `red` on `dominant` is the one worth saying out loud: it is NOT a
 * severity. The tile states which trigger fired most often, never how bad it
 * was, and it is the row's only warm tile so that the row has one focal
 * point rather than five.
 */
export type FactTone = 'blue' | 'red' | 'violet' | 'amber' | 'slate'

export interface ClusterFact {
  key: FactKey
  tone: FactTone
  label: string
  value: string
  /** Set in the mono face: an identifier the client can paste into Zabbix,
   *  not prose. A tag is; the design sets a group name and a trigger name in
   *  the display face, at the size of the tile's answer. */
  mono?: boolean
  /** The fraction line, in PARTS so the counts can be set apart by weight.
   *  Parts and not a finished string for the same reason `reachParts` is:
   *  hunting digits in a sentence bolded the `02` in `nas-synology-02`. */
  stat?: ReachPart[]
  /** One sentence saying what the stat means, at a footnote's weight. */
  note?: string
  /** Qualifier lines under the value, in order. */
  sub?: string[]
  /** The earlier occurrences, on the `matches` tile only. */
  history?: MatchHistory
}

/** How many earlier occurrences the tile lists before it is opened. */
export const MATCH_RECENT = 3

/** Where the server used to stop the list, with no total beside it. A row
 *  from then holding exactly this many is a FLOOR, and says so. */
export const LEGACY_MATCH_CAP = 5

export const SHOW_FEWER = 'Show fewer'

export interface MatchDay { at: number; count: number; label: string }

/**
 * The "Seen before" tile's material, for any number of occurrences.
 *
 * A list of dates answers "is this a one-off or a routine?" well at three
 * rows and badly at eighteen, so past MATCH_RECENT the list is cut and a day
 * strip carries the whole set instead. The strip appears exactly when the
 * list is cut: below that the rows already say everything it would.
 *
 * The total is the server's, never the list's length. The list was capped at
 * five with no total beside it, so a pattern seen forty times said "5 times
 * in 30 days" — a cap presented as a count.
 */
export interface MatchHistory {
  total: number
  /** The total is a floor: a row from before `matches_total` existed, whose
   *  list sits at the old cap. */
  atLeast: boolean
  /** Null when the tile draws no strip. */
  days: MatchDay[] | null
  /** Every listed occurrence, most recent first. */
  rows: string[]
  /** How many rows show while the tile is closed. */
  recent: number
  /** Occurrences counted in `total` that the list does not carry. */
  unlisted: number
}

/** A day on the environment's clock, month and day only. */
export function dayLabel(epoch: number, tz?: string | null): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  const d = new Date(epoch * 1000)
  try {
    return d.toLocaleDateString([], tz ? { ...opts, timeZone: tz } : opts)
  } catch {
    return d.toLocaleDateString([], opts)
  }
}

export function dayReadout(d: MatchDay): string {
  const n = d.count === 0 ? 'not seen' : d.count === 1 ? 'once' : `${d.count} times`
  return `${d.label} · ${n}`
}

export function matchHistory(row: AlertClusterRow): MatchHistory | null {
  const list = row.matches ?? []
  if (list.length === 0) return null
  const known = typeof row.matches_total === 'number'
  const total = known ? Math.max(row.matches_total as number, list.length) : list.length
  const days = total > MATCH_RECENT && row.match_days?.length
    ? row.match_days.map((d) => ({ ...d, label: dayLabel(d.at, row.tz) }))
    : null
  return {
    total,
    atLeast: !known && list.length >= LEGACY_MATCH_CAP,
    days,
    rows: list.map(
      (m) => `${clockAt(m.first_alert_at, row.tz, true)} · ${plural(m.alerts_total, 'alert')}`,
    ),
    recent: Math.min(MATCH_RECENT, list.length),
    unlisted: total - list.length,
  }
}

/** The control that opens the rest of the list. Counts the rows it reveals,
 *  not the total, because those are what appear when it is pressed. */
export function moreLabel(h: MatchHistory): string {
  return `Show ${h.rows.length - h.recent} earlier`
}

export function unlistedNote(h: MatchHistory): string {
  return `Listing the ${h.rows.length} most recent of ${h.total}.`
}

/**
 * What the group tile's footnote says about the hosts it counted.
 *
 * Names the window's LENGTH, which is the thing the tile is otherwise silent
 * about: "5 of 29 hosts" is a fraction of an estate and says nothing about
 * how fast it happened. A cluster with no countable span (under a minute, or
 * still live and therefore without an end) drops the adjective rather than
 * writing "in this under a minute window".
 */
/**
 * The group tile's footnote when the cluster is ONE host.
 *
 * Names the host, which the row that opened this dialog deliberately does not
 * — so the two surfaces divide the work rather than repeating it: the row
 * says how many, the dialog says which. Without the name this note would be
 * "The alerting host belongs to this group", a sentence whose subject the
 * reader has to go and find in the headline above.
 */
export function soleHostNote(row: AlertClusterRow): string {
  const host = row.hosts?.[0]
  return host
    ? `${host} belongs to this group.`
    : 'The one alerting host belongs to this group.'
}

export function groupNote(row: AlertClusterRow): string {
  const hosts = plural(row.hosts_total, 'host')
  const span = row.state === 'live'
    ? null : spanAdjective(row.last_alert_at - row.first_alert_at)
  return `These ${hosts} generated the alerts in this ${span ? `${span} ` : ''}window.`
}

/**
 * The facts, in reading order, for the tiles that replaced the chain.
 *
 * ORDER. `hosts` is deliberately absent: the headline already says "across 5
 * hosts", and a tile repeating it was the one that told a reader nothing
 * new. `group` leads, by the 2026-09-14 design — WHO was hit before WHAT hit
 * them, because the group is the tile a reader can act on (it names a set of
 * machines they own) while the trigger name is the one they can only read.
 * Then the connective facts, with `cause` kept in terminal position among
 * them: it is the card's strongest claim, and promoting it would rank it
 * above the hedge that qualifies it. History (`matches`) is last.
 */
export function facts(
  row: AlertClusterRow, fleetHosts?: number | null,
): ClusterFact[] {
  const b = blocks(row)
  const out: ClusterFact[] = []

  if (b.group) {
    out.push({
      key: 'group',
      tone: 'blue',
      label: 'Affected host group',
      value: row.host_group_name ?? '',
      // Only a fraction when the group could actually hold the cluster
      // (groupHoldsCluster): the server picks the group most of the hosts
      // share, which at the fleet rung can be smaller than the cluster, and
      // "68 of 41" is a fraction whose bottom half is smaller than its top.
      stat: b.groupFraction
        ? [
          { text: String(row.hosts_total), strong: true },
          { text: ' of ' },
          { text: String(row.host_group_total), strong: true },
          { text: ` ${row.host_group_total === 1 ? 'host' : 'hosts'} affected` },
        ]
        : undefined,
      note: b.groupFraction
        ? groupNote(row)
        : row.rung === 'narrow'
          ? soleHostNote(row)
          : 'Most of the alerting hosts belong to it.',
    })
  }

  if ((row.dominant_count ?? 0) > 0) {
    out.push({
      key: 'dominant',
      tone: 'red',
      label: 'Most common alert',
      value: row.dominant_label ?? '',
      stat: [
        { text: String(row.dominant_count), strong: true },
        { text: ' of ' },
        { text: String(row.alerts_total), strong: true },
        { text: row.alerts_total === 1 ? ' alert' : ' alerts' },
      ],
      note: 'This alert occurred most often during this window.',
    })
  }

  if (b.tags) {
    const tags = row.shared_tags ?? []
    out.push({
      key: 'tags',
      tone: 'violet',
      label: tags.length === 1 ? 'Shared tag' : 'Shared tags',
      value: tags.map((t) => (t.value ? `${t.tag}: ${t.value}` : t.tag)).join('  '),
      mono: true,
      note: `Carried by all ${plural(row.hosts_total, 'host')} in the window.`,
    })
  }

  if (b.cause) {
    out.push({
      key: 'cause',
      tone: 'amber',
      // Named as a change BEFORE the alerts, because that is the only kind
      // this tile may hold (ruling 8) — and saying so is what lets a reader
      // judge the claim instead of taking it.
      label: 'Change before the alerts',
      value: row.cause?.label ?? '',
      note: row.cause?.at
        ? `Recorded ${clockAt(row.cause.at, row.tz)}, before the first alert.`
        : 'Recorded before the first alert.',
    })
  }

  const history = b.matches ? matchHistory(row) : null
  if (history) {
    const seenOn = history.days?.filter((d) => d.count > 0).length ?? 0
    out.push({
      key: 'matches',
      tone: 'slate',
      label: 'Seen before',
      value: history.atLeast
        ? `${history.total} or more times in 30 days`
        : history.total === 1
          ? 'Once in 30 days'
          : `${history.total} times in 30 days`,
      // The strip's plain-language reading, in the stat slot every other
      // tile uses for its fraction. The strip itself is hidden from
      // assistive tech; this line is what it says.
      stat: history.days
        ? [
          { text: 'On ' },
          { text: String(seenOn), strong: true },
          { text: ' of the last ' },
          { text: String(history.days.length), strong: true },
          { text: ' days' },
        ]
        : undefined,
      history,
      note: NO_MATCH_NOTE,
    })
  }

  // fleetHosts is accepted so this stays the one place the card's facts are
  // decided, even though no fact needs it today: the blast-radius fraction
  // it feeds lives in the headline (reachPhrase) and the fleet hedge.
  void fleetHosts
  return out
}

/**
 * The label over the tiles.
 *
 * It said "What Netwatch found", which named the AUTHOR of the findings. The
 * 2026-09-14 design names their SCOPE instead, and that is the more useful
 * of the two here: the chart directly above spends most of its width on 24
 * hours of context, so the one thing a reader needs before reading "3 of 8"
 * is which of those two spans the 8 came from.
 */
export const FACTS_LABEL = 'During this affected window'

/** The scope line under it, with the window spelled out as prose. */
export function factsNote(row: AlertClusterRow): string {
  const alerts = plural(row.alerts_total, 'alert')
  return row.state === 'live'
    ? `Key findings for the ${alerts} since ${clockAt(row.first_alert_at, row.tz)}`
    : `Key findings for the ${alerts} between `
      + formatWindow(row.first_alert_at, row.last_alert_at, row.tz, ' and ')
}

/** Behind the (i) beside that label: the distinction the whole band turns on. */
export const FACTS_HINT =
  'Counted from the alerts inside the affected window only, never from the '
  + '24 hours of context in the chart above.'

export interface ClusterGap {
  key: 'cause' | 'matches' | 'volume'
  /** Names the question that came back empty, as a heading. */
  title: string
  /** What the row shows: the rule that was applied where it fits on one
   *  line, and a brief where it does not. */
  note: string
  /** The rule in full, behind an (i), for the row whose note is a brief. An
   *  absence stated without its rule reads as a verdict on the environment. */
  hint?: string
}

/**
 * The questions that came back empty, in the same order their answers would
 * have appeared in `facts`.
 *
 * Always both kinds or neither, never silently fewer: a card that dropped the
 * cause question when it found nothing left a reader unable to tell "looked,
 * found nothing" from "never looked", which is the bug the empty tiles were
 * introduced to fix — and which this band, not those tiles, now fixes.
 */
export function gaps(row: AlertClusterRow): ClusterGap[] {
  const b = blocks(row)
  const out: ClusterGap[] = []
  if (b.cause === false) {
    out.push({ key: 'cause', title: NO_CAUSE_TITLE, note: NO_CAUSE_NOTE })
  }
  if (b.matches === false) {
    out.push({
      key: 'matches', title: NO_MATCH_TITLE,
      note: NO_MATCH_BRIEF, hint: NO_MATCH_NOTE,
    })
  }
  const vol = volumeGap(row)
  if (vol) out.push({ key: 'volume', ...vol })
  return out
}
